import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import ProductCard from "@/components/ProductCard";
import { Seo, SITE_URL } from "@/components/Seo";
import { ProductPrice } from "@/components/ProductPrice";
import { resolveImgUrl, imgSrcSet } from "@/components/ImageUploader";
import HeroCarousel from "@/components/HeroCarousel";
import SignupModal from "@/components/SignupModal";
import { StarRating } from "@/components/Reviews";
import { Button } from "@/components/ui/button";
import { Sparkles, Truck, Award, Heart, ArrowRight, Scissors, Palette, Flame, ChevronLeft, ChevronRight } from "lucide-react";

// All hero/category/CTA imagery now lives in Emergent Object Storage (paths returned by /api/files/...)
const IMG = {
  GIRLS_1: "/api/files/shine-fabric/products/6fb8b839678540c2b3fe0d53e825b9a4.jpg",
  GIRLS_4: "/api/files/shine-fabric/products/19a04c8b0f9f4b0e971fc6a2d1f5abbb.jpg",
  GIRLS_7: "/api/files/shine-fabric/products/2ba0e2ab85164032982f4afcdfc9f4ca.jpg",
  GIRLS_9: "/api/files/shine-fabric/products/a5087fd9900247199b8ac490e323ea8b.jpg",
  BOYS_1: "/api/files/shine-fabric/products/574cbe06497d4bdf863a39665ceee575.jpg",
  BOYS_4: "/api/files/shine-fabric/products/dae5d2db8d1f404481a8d790642563bd.jpg",
  BOYS_7: "/api/files/shine-fabric/products/86682ac535864fc08196258332a9378b.jpg",
  BOYS_10: "/api/files/shine-fabric/products/0e378d9c182049169bd2493e790eece9.jpg",
};

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [trending, setTrending] = useState([]);
  const [content, setContent] = useState(null);
  const [heroBanners, setHeroBanners] = useState([]);
  const trendRef = useRef(null);
  const { addItem } = useCart();

  useEffect(() => {
    api.get("/products?featured=true").then(async (r) => {
      let list = r.data || [];
      // Fallback: if no products are flagged featured (common in fresh prod DB),
      // show the newest products so the "Bestsellers" section never stays empty.
      if (list.length === 0) {
        try {
          const r2 = await api.get("/products?sort=newest");
          list = r2.data || [];
        } catch (e) { console.warn("Failed to load fallback products", e); }
      }
      setFeatured(list.slice(0, 8));
    });
    api.get("/products/trending?limit=10").then((r) => setTrending(r.data));
    api.get("/site-content").then((r) => setContent(r.data)).catch(() => {});
    api.get("/hero-banners").then((r) => setHeroBanners(r.data || [])).catch(() => setHeroBanners([]));
  }, []);

  // Auto-rotate trending strip
  useEffect(() => {
    if (!trendRef.current || trending.length === 0) return;
    const id = setInterval(() => {
      const el = trendRef.current;
      if (!el) return;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 10;
      el.scrollTo({ left: atEnd ? 0 : el.scrollLeft + 280, behavior: "smooth" });
    }, 4000);
    return () => clearInterval(id);
  }, [trending.length]);

  const scrollTrend = (dir) => {
    const el = trendRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 320, behavior: "smooth" });
  };

  return (
    <div className="overflow-x-hidden">
      <Seo
        title="SHINE FABRIC | Trendy, Comfy Clothes for Kids — Boys & Girls Wear"
        description="Shop premium kids' clothing at SHINE FABRIC — trendy frocks, t-shirts, sets & ethnic wear for Chotta Boyz & Chotti Girls. Free shipping on all orders, made with love in Tirupur, India."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "SHINE FABRIC",
          url: SITE_URL,
          potentialAction: {
            "@type": "SearchAction",
            target: `${SITE_URL}/shop?q={search_term_string}`,
            "query-input": "required name=search_term_string",
          },
        }}
      />
      {/* HERO CAROUSEL */}
      <div className="h-[52vh] min-h-[340px] sm:h-[62vh] sm:min-h-[440px] lg:h-[600px]">
        <HeroCarousel slides={heroBanners} />
      </div>

      {/* TRENDING NOW */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14" data-testid="trending-section">
        <div className="flex items-end justify-between mb-6 sm:mb-8">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-orange-500 font-bold">
              <Flame className="w-4 h-4 animate-pulse"/> Trending now
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold mt-2">What everyone's loving</h2>
            <p className="text-sm text-slate-500 mt-2">Updated live · based on shopper activity this week</p>
          </div>
          <div className="hidden sm:flex gap-2">
            <button onClick={() => scrollTrend(-1)} className="w-10 h-10 rounded-full border border-slate-200 hover:bg-slate-100 flex items-center justify-center" data-testid="trending-prev"><ChevronLeft className="w-4 h-4"/></button>
            <button onClick={() => scrollTrend(1)} className="w-10 h-10 rounded-full border border-slate-200 hover:bg-slate-100 flex items-center justify-center" data-testid="trending-next"><ChevronRight className="w-4 h-4"/></button>
          </div>
        </div>
        <div ref={trendRef} className="flex gap-4 sm:gap-5 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
          {trending.length === 0
            ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="w-64 flex-shrink-0 aspect-[3/4] shimmer rounded-2xl"/>)
            : trending.map((p, i) => {
                const tone = p.category === "girls" ? "pink" : "sky";
                return (
                  <Link to={`/product/${p.product_id}`} key={p.product_id} data-testid={`trending-card-${p.product_id}`}
                    className="group relative w-60 sm:w-64 flex-shrink-0 snap-start bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all overflow-hidden">
                    <div className="relative aspect-[3/4] bg-slate-100 overflow-hidden">
                      <img src={resolveImgUrl(p.images?.[0], 600)}
                        srcSet={imgSrcSet(p.images?.[0], [200, 300, 400, 600])}
                        sizes="(max-width: 640px) 60vw, 256px"
                        alt={p.name} loading="lazy"
                        className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-110"/>
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-orange-500 text-white">
                          <Flame className="w-3 h-3"/> #{i + 1}
                        </span>
                        <span className={`text-[10px] uppercase tracking-[0.15em] font-bold ${tone === "pink" ? "text-pink-500" : "text-sky-500"}`}>
                          {p.category === "girls" ? "Chotti Girls" : "Chotta Boyz"}
                        </span>
                      </div>
                      <h3 className="font-display font-medium text-slate-900 line-clamp-2 mt-1.5 min-h-[2.5rem]">{p.name}</h3>
                      {p.review_count > 0 && (
                        <div className="flex items-center gap-1.5 mt-1" data-testid={`trending-rating-${p.product_id}`}>
                          <StarRating value={p.avg_rating} size={14} />
                          <span className="text-xs text-slate-400">({p.review_count})</span>
                        </div>
                      )}
                      {p.trending_events > 0 && (
                        <div className="text-[10px] uppercase tracking-wider font-semibold text-orange-500 mt-1">
                          🔥 {p.trending_events} this week
                        </div>
                      )}
                      <ProductPrice price={p.price} mrp={p.mrp} size="sm" showDiscount className="mt-2" />
                    </div>
                  </Link>
                );
              })}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="grid md:grid-cols-2 gap-6">
          {[
            { cat: "girls", title: "Chotti Girls", subtitle: "Frocks, gowns & more", img: resolveImgUrl(content?.cat_girls_img || IMG.GIRLS_1), grad: "brand-gradient-pink" },
            { cat: "boys", title: "Chotta Boyz", subtitle: "Sets, shirts & combos", img: resolveImgUrl(content?.cat_boys_img || IMG.BOYS_1), grad: "brand-gradient-sky" },
          ].map((c) => (
            <Link key={c.cat} to={`/shop?category=${c.cat}`} data-testid={`category-${c.cat}`}
              className="relative group rounded-3xl overflow-hidden shadow-md hover:shadow-2xl transition-shadow aspect-[16/10]">
              <img src={c.img} alt={c.title} width={1280} height={800} loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"/>
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"/>
              <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 text-white">
                <div className="text-xs font-bold uppercase tracking-[0.2em] opacity-80">Shop</div>
                <div className="font-display text-3xl sm:text-4xl font-semibold mt-1">{c.title}</div>
                <div className="text-sm opacity-90 mt-1">{c.subtitle}</div>
                <div className="mt-4 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider"><span>Browse</span><ArrowRight className="w-4 h-4"/></div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-end justify-between mb-8 sm:mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-pink-500 font-bold">Trending</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold mt-2">Bestsellers this season</h2>
          </div>
          <Link to="/shop" className="hidden sm:inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider hover:text-pink-500 transition-colors">View all<ArrowRight className="w-4 h-4"/></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {featured.map((p) => <ProductCard key={p.product_id} product={p} onAdd={(prod) => addItem(prod, 1, prod.sizes?.[0])}/>)}
          {featured.length === 0 && Array.from({length: 8}).map((_, i) => (
            <div key={i} className="aspect-[3/4] shimmer rounded-2xl"/>
          ))}
        </div>
      </section>

      {/* PROCESS / WHY */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="text-center mb-12">
          <div className="text-xs uppercase tracking-[0.18em] text-sky-500 font-bold">Our craft</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold mt-2">Why parents trust Shine Fabric</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { icon: Award, title: "Hand-picked premium fabric", desc: "Soft on skin, breathable, easy to wash. Sourced and quality-checked.", grad: "brand-gradient-pink" },
            { icon: Palette, title: "Designed with imagination", desc: "Trendy patterns, cheerful colours, thoughtful details — kids love them.", grad: "brand-gradient-sky" },
            { icon: Scissors, title: "Master tailoring", desc: "Each garment cut and sewn by experienced artisans for a perfect fit.", grad: "brand-gradient-sunny" },
            { icon: Heart, title: "Loved by 50,000+ kids", desc: "Generations of kids have grown up wearing Shine Fabric outfits.", grad: "brand-gradient-pink" },
            { icon: Truck, title: "Fast pan-India delivery", desc: "Free shipping on all orders. Tracked dispatch within 48 hours.", grad: "brand-gradient-sky" },
            { icon: Sparkles, title: "Excellence in every stitch", desc: "Final refinement and finishing that makes every piece stand out.", grad: "brand-gradient-sunny" },
          ].map((f, i) => (
            <div key={i} className="bg-white rounded-3xl border border-slate-100 p-6 shadow-sm hover:shadow-lg transition-shadow">
              <div className={`w-12 h-12 rounded-2xl ${f.grad} flex items-center justify-center text-white mb-4 shadow-md`}>
                <f.icon className="w-5 h-5"/>
              </div>
              <h3 className="font-display text-xl font-semibold">{f.title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      {content?.cta_enabled !== false && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="rounded-3xl bg-slate-900 text-white px-6 sm:px-12 py-12 lg:py-16 grid md:grid-cols-2 items-center gap-8 overflow-hidden relative">
            <div className="relative z-10">
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">{content?.cta_headline || "Ready for cuteness overload?"}</h2>
              <p className="mt-4 text-slate-300 max-w-md">{content?.cta_subtitle || "Discover the new collection — limited stock, ships in 48 hours."}</p>
              <Button asChild className="mt-7 rounded-full brand-gradient-sunny text-slate-900 border-0 h-12 px-7 btn-bouncy font-bold" data-testid="cta-shop">
                <Link to="/shop">Shop now<ArrowRight className="w-4 h-4 ml-2"/></Link>
              </Button>
            </div>
            <div className="relative z-10 hidden md:grid grid-cols-2 gap-3">
              <img src={resolveImgUrl(IMG.GIRLS_9)} width={400} height={400} loading="lazy" className="rounded-2xl aspect-square object-cover" alt=""/>
              <img src={resolveImgUrl(IMG.BOYS_10)} width={400} height={400} loading="lazy" className="rounded-2xl aspect-square object-cover mt-8" alt=""/>
            </div>
            <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-pink-500/30 blur-3xl"/>
            <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-sky-500/20 blur-3xl"/>
          </div>
        </section>
      )}
      <SignupModal />
    </div>
  );
}
